/**
 * First-discovery reveal ceremony for the evolution dial / strip.
 *
 * Every first-time tier discovery (lifetime, not per run) plays in the Merge
 * (Shake) stage: sim pauses, a circular spotlight vignette fades in (no flat
 * full-screen dim), the new creature bounces in at centre with spinning rays
 * and a "New Discovery!" headline, holds briefly, then the vignette/rays fade
 * and the chip flies into its slot on the dial/strip. Hub + counter update
 * on land.
 *
 * When the discovered tier is the gate tier (T-rex) a teaser phase follows:
 * the whole dial elevates toward screen centre, a slow light wave runs across
 * the chips, the chips squeeze aside to open an 11th slot, and a "?" chip
 * materialises at centre and flies into the new slot.
 */
import { mergeTierImageUrl } from "../assets/merge-tier-urls.js";
import { largeTierImageUrl } from "./toon/art-catalog.js";
import {
  createRingSecretChipNode,
  getRingChipStepDeg,
  RING_BASE_SLOTS,
  RING_MAX_CHIPS,
} from "./evolution-ring-dom.js";
import { createStripSecretChipNode } from "./evolution-strip-dom.js";

export var DISCOVERY_REVEAL_TIMING = {
  /**
   * Beat between the merge and the ceremony, so the merge flash, blast and the new creature's
   * pop-in read before the vignette dims the jar (the jar holds still meanwhile: the reveal
   * hold already freezes the sim, which reads as a hit-stop). Presentation timing only.
   */
  MERGE_SETTLE_MS: 400,
  /** Circular vignette fade-in (no flat full-screen dim). */
  VIGNETTE_IN_MS: 280,
  /** Legacy alias used by overlay opacity transition. */
  SCRIM_IN_MS: 125,
  /** Showcase creature bounce scale 0→1 at screen centre. */
  SHOWCASE_IN_MS: 480,
  /** Default materialise for non-showcase flyers (e.g. teaser "?"). */
  CHIP_IN_MS: 190,
  /** Hold showcase (creature + rays + headline) before flight. Dead air — keep it short. */
  CHIP_HOLD_MS: 750,
  /** Fade vignette / shrink rays / fade headline before flight. */
  SHOWCASE_OUT_MS: 360,
  /** Flying chip travel into its slot (~30% longer than the original 260 ms). */
  CHIP_FLY_MS: 338,
  LAND_SETTLE_MS: 100,
  SCRIM_OUT_MS: 125,
  /** Centre hub bounce when the discovery chip lands (ring layout). */
  HUB_POP_MS: 140,
  /** Vertical strip: run-max scale/glow handoff when the discovery chip lands. */
  STRIP_RUN_MAX_MS: 190,
  /** Teaser phase (gate tier only) — after the T-rex discovery fly-in. */
  TEASER_GAP_MS: 200,
  DIAL_UP_MS: 380,
  WAVE_STEP_MS: 115,
  WAVE_FLASH_MS: 390,
  SQUEEZE_MS: 450,
  DIAL_DOWN_MS: 350,
  /** "?" chip materialise / fly into the squeezed 11th slot. */
  TEASER_CHIP_IN_MS: 290,
  TEASER_CHIP_FLY_MS: 390,
  TEASER_LAND_SETTLE_MS: 150,
  TEASER_LAND_FLASH_MS: 470,
  TEASER_COUNT_POP_MS: 210,
};

export var DISCOVERY_SHOWCASE_HEADLINE = "New Discovery!";

/**
 * @param {object[]} chain
 * @param {number} tierId
 * @returns {"teaser"|"dragon"|null}
 */
export function getSecretRevealMode(chain, tierId) {
  if (!chain || chain.length < 2 || tierId == null || tierId === undefined) return null;
  var finalIndex = chain.length - 1;
  var gateTier = chain[finalIndex - 1];
  var finalTier = chain[finalIndex];
  if (!gateTier || !finalTier) return null;
  if (tierId === gateTier.id) return "teaser";
  if (tierId === finalTier.id) return "dragon";
  return null;
}

/**
 * Ceremony step list (pure, for tests and reduced-motion branching).
 *
 * @param {boolean} withTeaser
 * @param {boolean} [reducedMotion]
 * @returns {{ type: string }[]}
 */
export function buildDiscoveryTimeline(withTeaser, reducedMotion) {
  if (reducedMotion) {
    return [{ type: "finish" }];
  }
  var steps = [
    { type: "vignetteIn" },
    { type: "chipMaterialize" },
    { type: "chipHold" },
    { type: "showcaseOut" },
    { type: "chipFly" },
    { type: "land" },
  ];
  if (withTeaser) {
    steps.push(
      { type: "dialUp" },
      { type: "wave" },
      { type: "squeeze" },
      { type: "secretChipMaterialize" },
      { type: "secretChipFly" },
      { type: "dialDown" }
    );
  }
  steps.push({ type: "scrimOut" });
  return steps;
}

/**
 * Victory-style word-pop spans for the discovery headline.
 * @param {HTMLElement | null} headlineEl
 * @param {string} [text]
 * @param {Document} [doc]
 */
export function fillDiscoveryHeadline(headlineEl, text, doc) {
  if (!headlineEl) return;
  var headlineText = text || DISCOVERY_SHOWCASE_HEADLINE;
  headlineEl.textContent = "";
  headlineEl.setAttribute("aria-label", headlineText);
  doc = doc || (typeof document !== "undefined" ? document : null);
  if (!doc || typeof doc.createElement !== "function") {
    headlineEl.textContent = headlineText;
    return;
  }
  var words = headlineText.split(/\s+/).filter(Boolean);
  for (var w = 0; w < words.length; w++) {
    if (w > 0) headlineEl.appendChild(doc.createTextNode(" "));
    var wordSpan = doc.createElement("span");
    wordSpan.className = "victory-overlay__headline-word";
    wordSpan.textContent = words[w];
    // The lit fill layer of the inked lettering reads its text from here.
    wordSpan.setAttribute("data-text", words[w]);
    wordSpan.style.animationDelay = (0.12 + w * 0.14) + "s";
    headlineEl.appendChild(wordSpan);
  }
}

/**
 * Total light-wave duration for a chip count (slow chase, one flash per chip).
 *
 * @param {number} chipCount
 */
export function computeWaveDurationMs(chipCount) {
  var n = Math.max(0, Math.floor(Number(chipCount) || 0));
  return n * DISCOVERY_REVEAL_TIMING.WAVE_STEP_MS + DISCOVERY_REVEAL_TIMING.WAVE_FLASH_MS;
}

/**
 * Transform that lifts the whole evolution panel over the scrim toward screen
 * centre. Translate first (origin centre), then scale about its own centre.
 *
 * @param {{ left: number, top: number, width: number, height: number }} rect
 * @param {number} viewportW
 * @param {number} viewportH
 * @param {boolean} isVertical
 * @returns {{ dx: number, dy: number, scale: number }}
 */
export function computeDialElevation(rect, viewportW, viewportH, isVertical) {
  var cx = rect.left + rect.width / 2;
  var cy = rect.top + rect.height / 2;
  var dx = viewportW / 2 - cx;
  var dy = viewportH / 2 - cy;
  var scale;
  if (isVertical) {
    scale = rect.width > 0 ? (viewportW * 0.92) / rect.width : 1;
    scale = Math.max(1, Math.min(1.6, scale));
  } else {
    var targetSize = Math.min(viewportW, viewportH) * 0.62;
    scale = rect.width > 0 ? targetSize / rect.width : 1;
    scale = Math.max(1, Math.min(2.2, scale));
  }
  return { dx: dx, dy: dy, scale: scale };
}

/**
 * @param {boolean} isVertical
 */
export function getRevealChipSelector(isVertical) {
  return isVertical ? ".evo-strip__node" : ".evo-ring__node";
}

/**
 * @param {boolean} isVertical
 * @param {number} tierIndex chain index
 */
export function getRevealTargetSelector(isVertical, tierIndex) {
  var base = isVertical ? ".evo-strip__node" : ".evo-ring__node";
  return base + '[data-tier-index="' + tierIndex + '"]';
}

/**
 * Flying chip shown at screen centre before it lands on the dial/strip.
 *
 * @param {Document} doc
 * @param {"creature"|"question"} kind
 * @param {string} [assetId]
 * @returns {HTMLElement}
 */
export function createFlyingChipElement(doc, kind, assetId) {
  doc = doc || document;
  var el = doc.createElement("div");
  el.className = "discovery-flyer";
  if (kind === "question") {
    el.classList.add("discovery-flyer--question");
    var mark = doc.createElement("span");
    mark.className = "discovery-flyer__mark";
    mark.textContent = "?";
    el.appendChild(mark);
  } else {
    var img = doc.createElement("img");
    img.className = "discovery-flyer__img";
    img.src = largeTierImageUrl(assetId || "cell");
    img.alt = "";
    el.appendChild(img);
  }
  return el;
}

/**
 * @param {DOMRect} from
 * @param {DOMRect} to
 * @returns {string}
 */
export function computeFlipTransform(from, to) {
  var dx = from.left + from.width / 2 - (to.left + to.width / 2);
  var dy = from.top + from.height / 2 - (to.top + to.height / 2);
  var sx = from.width / Math.max(1, to.width);
  var sy = from.height / Math.max(1, to.height);
  return "translate(" + dx + "px," + dy + "px) scale(" + sx + "," + sy + ")";
}

/**
 * @param {HTMLElement[]} nodes
 * @returns {DOMRect[]}
 */
export function captureNodeRects(nodes) {
  var rects = [];
  for (var i = 0; i < nodes.length; i++) {
    rects.push(nodes[i].getBoundingClientRect());
  }
  return rects;
}

/**
 * FLIP squeeze: animate strip chips from their previous layout into the
 * squeezed 11-column grid.
 *
 * @param {HTMLElement} panel
 * @param {DOMRect[]} prevRects
 * @param {number} durationMs
 */
export function applyStripSqueezeFlip(panel, prevRects, durationMs) {
  if (!panel || !prevRects || prevRects.length === 0) return;
  var nodes = panel.querySelectorAll(".evo-strip__node");
  if (!nodes.length) return;
  durationMs = durationMs || DISCOVERY_REVEAL_TIMING.SQUEEZE_MS;

  for (var i = 0; i < nodes.length && i < prevRects.length; i++) {
    var node = nodes[i];
    var next = node.getBoundingClientRect();
    var prev = prevRects[i];
    var tx = prev.left + prev.width / 2 - (next.left + next.width / 2);
    var ty = prev.top + prev.height / 2 - (next.top + next.height / 2);
    var sx = prev.width / Math.max(1, next.width);
    var sy = prev.height / Math.max(1, next.height);
    node.style.transition = "none";
    node.style.transform =
      "translate(" + tx + "px," + ty + "px) scale(" + sx + "," + sy + ")";
  }

  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      for (var j = 0; j < nodes.length && j < prevRects.length; j++) {
        nodes[j].style.transition =
          "transform " + durationMs + "ms cubic-bezier(0.34, 1.25, 0.64, 1)";
        nodes[j].style.transform = "";
      }
    });
  });
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function waitMs(ms) {
  return new Promise(function (resolve) {
    setTimeout(resolve, ms);
  });
}

function nextFrame() {
  return new Promise(function (resolve) {
    requestAnimationFrame(function () {
      requestAnimationFrame(resolve);
    });
  });
}

/**
 * Places the flyer at screen centre (hidden), then fades/scales it in.
 * Used for the teaser "?" chip (compact).
 */
function materializeFlyer(flyer, viewportW, viewportH, chipInMs) {
  var inMs = chipInMs != null ? chipInMs : DISCOVERY_REVEAL_TIMING.CHIP_IN_MS;
  var size = Math.min(viewportW, viewportH) * 0.18;
  size = Math.max(56, Math.min(150, size));
  flyer.style.left = viewportW / 2 + "px";
  flyer.style.top = viewportH / 2 + "px";
  flyer.style.width = size + "px";
  flyer.style.height = size + "px";
  flyer.style.transform = "translate(-50%, -50%) scale(0.4)";
  flyer.style.opacity = "0";

  return nextFrame().then(function () {
    flyer.style.transition =
      "transform " + inMs + "ms cubic-bezier(0.22, 1.2, 0.36, 1), "
      + "opacity " + inMs + "ms ease-out";
    flyer.style.opacity = "1";
    flyer.style.transform = "translate(-50%, -50%) scale(1)";
    return waitMs(inMs);
  });
}

/**
 * Showcase bounce: scale 0 → 1 with spring overshoot over the creature slot.
 * @param {HTMLElement} flyer
 * @param {{ left: number, top: number, width: number, height: number }} slotRect
 * @param {number} [chipInMs]
 */
function materializeShowcaseFlyer(flyer, slotRect, chipInMs) {
  var inMs = chipInMs != null ? chipInMs : DISCOVERY_REVEAL_TIMING.SHOWCASE_IN_MS;
  // Follow the layout-scaled slot; do not clamp to fixed CSS-pixel floors/ceilings.
  var size = Math.max(1, Math.min(slotRect.width, slotRect.height) || 160);
  var cx = slotRect.left + slotRect.width / 2;
  var cy = slotRect.top + slotRect.height / 2;
  flyer.classList.add("discovery-flyer--showcase");
  flyer.style.left = cx + "px";
  flyer.style.top = cy + "px";
  flyer.style.width = size + "px";
  flyer.style.height = size + "px";
  flyer.style.transform = "translate(-50%, -50%) scale(0)";
  flyer.style.opacity = "0";

  return nextFrame().then(function () {
    flyer.style.transition =
      "transform " + inMs + "ms cubic-bezier(0.34, 1.45, 0.64, 1), "
      + "opacity " + Math.min(220, inMs) + "ms ease-out";
    flyer.style.opacity = "1";
    flyer.style.transform = "translate(-50%, -50%) scale(1)";
    return waitMs(inMs);
  });
}

/**
 * @param {HTMLElement | null} overlay
 */
function clearDiscoveryShowcase(overlay) {
  if (!overlay) return;
  overlay.classList.remove(
    "secret-reveal-overlay--showcase",
    "secret-reveal-overlay--showcase-out"
  );
  overlay.style.removeProperty("--discovery-vignette-ms");
  overlay.style.removeProperty("--discovery-showcase-in-ms");
  overlay.style.removeProperty("--discovery-showcase-out-ms");
  var headline = overlay.querySelector("#secretRevealHeadline")
    || overlay.querySelector(".secret-reveal-showcase__headline");
  if (headline) {
    headline.textContent = "";
    headline.removeAttribute("aria-label");
  }
  var showcase = overlay.querySelector("#secretRevealShowcase")
    || overlay.querySelector(".secret-reveal-showcase");
  if (showcase) showcase.setAttribute("aria-hidden", "true");
}

/**
 * Flies the flyer from wherever it is into the target rect.
 */
function flyFlyerToRect(flyer, targetRect, chipFlyMs) {
  var d = chipFlyMs != null ? chipFlyMs : DISCOVERY_REVEAL_TIMING.CHIP_FLY_MS;
  flyer.style.transition =
    "left " + d + "ms cubic-bezier(0.25, 0.1, 0.25, 1), "
    + "top " + d + "ms cubic-bezier(0.25, 0.1, 0.25, 1), "
    + "width " + d + "ms cubic-bezier(0.25, 0.1, 0.25, 1), "
    + "height " + d + "ms cubic-bezier(0.25, 0.1, 0.25, 1), "
    + "opacity " + Math.min(280, d) + "ms ease-out";
  flyer.style.left = targetRect.left + targetRect.width / 2 + "px";
  flyer.style.top = targetRect.top + targetRect.height / 2 + "px";
  flyer.style.width = Math.max(1, targetRect.width) + "px";
  flyer.style.height = Math.max(1, targetRect.height) + "px";
  flyer.style.transform = "translate(-50%, -50%) scale(1)";
  return waitMs(d).then(function () {
    flyer.style.opacity = "0";
    return waitMs(Math.min(280, d));
  });
}

function flashNode(node, timers, flashMs) {
  if (!node) return;
  var d = flashMs != null ? flashMs : DISCOVERY_REVEAL_TIMING.TEASER_LAND_FLASH_MS;
  node.style.animationDuration = d + "ms";
  node.classList.add("evo-reveal-land-flash");
  var id = setTimeout(function () {
    node.classList.remove("evo-reveal-land-flash");
    node.style.animationDuration = "";
  }, d + 50);
  if (timers) timers.push(id);
}

/**
 * CSS custom properties for teaser-phase keyframes (wave, squeeze, land flash).
 *
 * @param {HTMLElement} panel
 * @param {typeof DISCOVERY_REVEAL_TIMING} T
 */
function applyTeaserCssVars(panel, T) {
  if (!panel) return;
  panel.style.setProperty("--evo-wave-flash-ms", T.WAVE_FLASH_MS + "ms");
  panel.style.setProperty("--evo-squeeze-ms", T.SQUEEZE_MS + "ms");
  panel.style.setProperty("--evo-land-flash-ms", T.TEASER_LAND_FLASH_MS + "ms");
  panel.style.setProperty("--evo-teaser-count-pop-ms", T.TEASER_COUNT_POP_MS + "ms");
}

function clearTeaserCssVars(panel) {
  if (!panel) return;
  panel.style.removeProperty("--evo-wave-flash-ms");
  panel.style.removeProperty("--evo-squeeze-ms");
  panel.style.removeProperty("--evo-land-flash-ms");
  panel.style.removeProperty("--evo-teaser-count-pop-ms");
}

/**
 * @param {number} current
 * @param {number} total
 * @returns {string}
 */
export function buildHubCountHtml(current, total) {
  return String(current)
    + "<span class=\"evo-ring__count-sep\">/</span>"
    + String(total);
}

/**
 * Reveal the ring/strip slot chip in place (no full panel rebuild).
 *
 * @param {HTMLElement} mount
 * @param {boolean} isVertical
 * @param {number} tierIndex
 * @param {string} assetId
 */
export function revealDiscoverySlot(mount, isVertical, tierIndex, assetId) {
  if (!mount) return;
  var sel = getRevealTargetSelector(isVertical, tierIndex);
  var node = mount.querySelector(sel);
  if (!node) return;
  node.classList.remove(
    isVertical ? "evo-strip__node--reveal-pending" : "evo-ring__node--reveal-pending"
  );
  node.classList.remove(
    isVertical ? "evo-strip__node--undiscovered" : "evo-ring__node--undiscovered"
  );
  var img = node.querySelector("img");
  if (!img) {
    img = document.createElement("img");
    node.appendChild(img);
  }
  img.src = mergeTierImageUrl(assetId || "cell");
  img.alt = "";
}

/**
 * Animate run-max scale + glow moving to the landed strip chip (vertical layout).
 *
 * @param {HTMLElement} mount
 * @param {{
 *   tierIndex: number,
 *   assetId: string,
 *   markRunMax?: boolean,
 *   onSwap?: function(): void,
 * }} opts
 * @returns {Promise<void>}
 */
export function animateDiscoveryStripRunMaxLand(mount, opts) {
  opts = opts || {};
  if (!mount) return Promise.resolve();

  var tierIndex = opts.tierIndex;
  var assetId = opts.assetId;
  var markRunMax = !!opts.markRunMax;
  var T = DISCOVERY_REVEAL_TIMING;

  revealDiscoverySlot(mount, true, tierIndex, assetId);

  var nextNode = mount.querySelector(getRevealTargetSelector(true, tierIndex));
  if (!nextNode) {
    if (typeof opts.onSwap === "function") opts.onSwap();
    return Promise.resolve();
  }

  if (!markRunMax) {
    if (typeof opts.onSwap === "function") opts.onSwap();
    return Promise.resolve();
  }

  if (prefersReducedMotion()) {
    var prevReduced = mount.querySelector(".evo-strip__node--run-max");
    if (prevReduced && prevReduced !== nextNode) {
      prevReduced.classList.remove("evo-strip__node--run-max");
    }
    nextNode.classList.add("evo-strip__node--run-max");
    if (typeof opts.onSwap === "function") opts.onSwap();
    return Promise.resolve();
  }

  var prevNode = mount.querySelector(".evo-strip__node--run-max");

  if (typeof opts.onSwap === "function") opts.onSwap();

  if (!prevNode || prevNode === nextNode) {
    nextNode.classList.add("evo-strip__node--run-max");
    return Promise.resolve();
  }

  nextNode.classList.add("evo-strip__node--run-max-in");
  prevNode.classList.add("evo-strip__node--run-max-out");
  void nextNode.offsetWidth;
  nextNode.classList.add("evo-strip__node--run-max");

  return new Promise(function (resolve) {
    setTimeout(function () {
      prevNode.classList.remove(
        "evo-strip__node--run-max",
        "evo-strip__node--run-max-out"
      );
      nextNode.classList.remove("evo-strip__node--run-max-in");
      resolve();
    }, T.STRIP_RUN_MAX_MS);
  });
}

/**
 * Animate the ring hub creature swap + counter bounce when a discovery chip lands.
 *
 * @param {HTMLElement} mount
 * @param {{
 *   assetId: string,
 *   countCurrent: number,
 *   countTotal: number,
 *   markRunMax?: boolean,
 *   onSwap?: function(): void,
 * }} opts
 * @returns {Promise<void>}
 */
export function animateDiscoveryHubLand(mount, opts) {
  opts = opts || {};
  if (!mount) return Promise.resolve();

  var hub = mount.querySelector(".evo-ring__hub");
  var hubImg = hub && hub.querySelector("img");
  var countEl = hub && hub.querySelector(".evo-ring__count");
  if (!hub || !hubImg) return Promise.resolve();

  var countHtml = buildHubCountHtml(opts.countCurrent, opts.countTotal);
  var T = DISCOVERY_REVEAL_TIMING;

  if (prefersReducedMotion()) {
    hubImg.src = mergeTierImageUrl(opts.assetId || "cell");
    if (countEl) countEl.innerHTML = countHtml;
    hub.classList.toggle("evo-ring__hub--run-max", !!opts.markRunMax);
    if (typeof opts.onSwap === "function") opts.onSwap();
    return Promise.resolve();
  }

  hubImg.src = mergeTierImageUrl(opts.assetId || "cell");
  hub.classList.toggle("evo-ring__hub--run-max", !!opts.markRunMax);
  if (countEl) {
    countEl.innerHTML = countHtml;
    countEl.classList.remove("evo-reveal-count-pop");
    void countEl.offsetWidth;
    countEl.classList.add("evo-reveal-count-pop");
  }
  if (typeof opts.onSwap === "function") opts.onSwap();

  hubImg.classList.remove("evo-reveal-hub-pop");
  void hubImg.offsetWidth;
  hubImg.classList.add("evo-reveal-hub-pop");

  return new Promise(function (resolve) {
    setTimeout(function () {
      hubImg.classList.remove("evo-reveal-hub-pop");
      if (countEl) countEl.classList.remove("evo-reveal-count-pop");
      resolve();
    }, T.HUB_POP_MS);
  });
}

/**
 * Slot + hub update when a discovery flyer lands (layout-aware).
 *
 * @param {HTMLElement} mount
 * @param {{
 *   isVertical: boolean,
 *   tierIndex: number,
 *   assetId: string,
 *   countCurrent: number,
 *   countTotal: number,
 *   markRunMax?: boolean,
 *   onSwap?: function(): void,
 * }} opts
 * @returns {Promise<void>}
 */
export function animateDiscoveryLanding(mount, opts) {
  opts = opts || {};
  if (opts.isVertical) {
    return animateDiscoveryStripRunMaxLand(mount, opts);
  }
  revealDiscoverySlot(mount, false, opts.tierIndex, opts.assetId);
  return animateDiscoveryHubLand(mount, opts);
}

/**
 * @param {{
 *   overlay: HTMLElement,
 *   popoutMount: HTMLElement,
 *   panelMount: HTMLElement,
 *   isVertical: boolean,
 *   tierIndex: number,
 *   assetId: string,
 *   withTeaser: boolean,
 *   secretTierIndex?: number,
 *   teaserCount?: { current: number, total: number },
 *   hubLandCountCurrent?: number,
 *   hubLandCountTotal?: number,
 *   hubLandRunMax?: boolean,
 *   onPopulateMid?: function(): void,
 *   onPopulateLanded?: function(): void,
 *   onPopulateFinal?: function(): void,
 *   onPlayAppearSound?: function(): void,
 *   onPlayLandSound?: function(): void,
 *   onPlaySound?: function(): void,
 *   onComplete?: function(): void,
 *   onAbort?: function(): void,
 * }} opts
 * @returns {{ abort: function(): void, done: Promise<void> }}
 */
export function runDiscoveryCeremony(opts) {
  opts = opts || {};
  var aborted = false;
  var finished = false;
  var timers = [];
  var elevated = null;

  function restoreElevatedPanel() {
    if (!elevated) return;
    var panel = elevated.panel;
    clearTeaserCssVars(panel);
    panel.classList.remove("evo-panel--elevated");
    panel.style.transition = "";
    panel.style.transform = "";
    panel.style.position = "";
    panel.style.left = "";
    panel.style.top = "";
    panel.style.width = "";
    panel.style.height = "";
    panel.style.margin = "";
    panel.style.zIndex = "";
    if (elevated.placeholder && elevated.placeholder.parentNode) {
      elevated.placeholder.parentNode.replaceChild(panel, elevated.placeholder);
    } else if (elevated.parent) {
      elevated.parent.appendChild(panel);
    }
    elevated = null;
  }

  function hideOverlay() {
    if (!opts.overlay) return;
    clearDiscoveryShowcase(opts.overlay);
    opts.overlay.classList.remove("secret-reveal-overlay--visible");
    opts.overlay.classList.add("hidden");
    opts.overlay.setAttribute("aria-hidden", "true");
  }

  function abort() {
    if (aborted || finished) return;
    aborted = true;
    for (var i = 0; i < timers.length; i++) clearTimeout(timers[i]);
    timers.length = 0;
    restoreElevatedPanel();
    if (opts.popoutMount) opts.popoutMount.innerHTML = "";
    hideOverlay();
    if (typeof opts.onPopulateFinal === "function") opts.onPopulateFinal();
    if (typeof opts.onAbort === "function") opts.onAbort();
  }

  var reduced = prefersReducedMotion();

  var done = (async function () {
    var overlay = opts.overlay;
    var popoutMount = opts.popoutMount;
    var panelMount = opts.panelMount;

    if (reduced || !overlay || !popoutMount || !panelMount) {
      finished = true;
      if (typeof opts.onPopulateFinal === "function") opts.onPopulateFinal();
      if (typeof opts.onComplete === "function") opts.onComplete();
      return;
    }

    var vw = window.innerWidth || 800;
    var vh = window.innerHeight || 600;
    var T = DISCOVERY_REVEAL_TIMING;

    // --- Phase A: centre showcase, then fly-in (all first-time tiers) -----
    overlay.classList.remove("hidden");
    overlay.setAttribute("aria-hidden", "false");
    clearDiscoveryShowcase(overlay);
    overlay.style.setProperty("--discovery-vignette-ms", T.VIGNETTE_IN_MS + "ms");
    overlay.style.setProperty("--discovery-showcase-in-ms", T.SHOWCASE_IN_MS + "ms");
    overlay.style.setProperty("--discovery-showcase-out-ms", T.SHOWCASE_OUT_MS + "ms");

    // Prepare the dial (placeholder slot, held hub) while the overlay is still
    // transparent — avoids a visible flash when the vignette is already dim.
    if (typeof opts.onPopulateMid === "function") opts.onPopulateMid();

    await nextFrame();
    if (aborted) return;
    overlay.classList.add("secret-reveal-overlay--visible");

    var showcase = overlay.querySelector("#secretRevealShowcase")
      || overlay.querySelector(".secret-reveal-showcase");
    var headline = overlay.querySelector("#secretRevealHeadline")
      || overlay.querySelector(".secret-reveal-showcase__headline");
    if (showcase) showcase.setAttribute("aria-hidden", "false");
    fillDiscoveryHeadline(headline, DISCOVERY_SHOWCASE_HEADLINE);

    overlay.classList.add("secret-reveal-overlay--showcase");
    await waitMs(T.VIGNETTE_IN_MS);
    if (aborted) return;

    var slotEl = overlay.querySelector(".secret-reveal-showcase__creature-slot");
    var slotRect = slotEl
      ? slotEl.getBoundingClientRect()
      : { left: vw / 2 - 80, top: vh / 2 - 80, width: 160, height: 160 };

    var flyer = createFlyingChipElement(document, "creature", opts.assetId);
    popoutMount.appendChild(flyer);
    if (typeof opts.onPlayAppearSound === "function") opts.onPlayAppearSound();
    await materializeShowcaseFlyer(flyer, slotRect, T.SHOWCASE_IN_MS);
    if (aborted) return;

    await waitMs(T.CHIP_HOLD_MS);
    if (aborted) return;

    overlay.classList.add("secret-reveal-overlay--showcase-out");
    await waitMs(T.SHOWCASE_OUT_MS);
    if (aborted) return;
    overlay.classList.remove(
      "secret-reveal-overlay--showcase",
      "secret-reveal-overlay--showcase-out"
    );
    if (showcase) showcase.setAttribute("aria-hidden", "true");
    if (headline) {
      headline.textContent = "";
      headline.removeAttribute("aria-label");
    }
    flyer.classList.remove("discovery-flyer--showcase");

    var target = panelMount.querySelector(
      getRevealTargetSelector(opts.isVertical, opts.tierIndex)
    );
    if (!target) {
      abort();
      return;
    }
    await flyFlyerToRect(flyer, target.getBoundingClientRect());
    if (aborted) return;
    if (typeof opts.onPlayLandSound === "function") opts.onPlayLandSound();

    if (flyer.parentNode) flyer.parentNode.removeChild(flyer);

    await animateDiscoveryLanding(panelMount, {
      isVertical: opts.isVertical,
      tierIndex: opts.tierIndex,
      assetId: opts.assetId,
      countCurrent: opts.hubLandCountCurrent != null ? opts.hubLandCountCurrent : opts.tierIndex + 1,
      countTotal: opts.hubLandCountTotal != null ? opts.hubLandCountTotal : opts.tierIndex + 1,
      markRunMax: !!opts.hubLandRunMax,
    });
    if (aborted) return;

    if (typeof opts.onPopulateLanded === "function") opts.onPopulateLanded();

    // --- Phase B: secret teaser (gate tier only) --------------------------
    if (opts.withTeaser) {
      await waitMs(T.TEASER_GAP_MS);
      if (aborted) return;

      var panel = panelMount.firstElementChild;
      if (panel) {
        // Lift the whole dial over the scrim toward screen centre.
        var rect = panel.getBoundingClientRect();
        var placeholder = document.createElement("div");
        placeholder.className = "secret-reveal-placeholder";
        placeholder.style.width = rect.width + "px";
        placeholder.style.height = rect.height + "px";
        panel.parentNode.insertBefore(placeholder, panel);
        popoutMount.appendChild(panel);
        elevated = { panel: panel, placeholder: placeholder, parent: panelMount };
        panel.classList.add("evo-panel--elevated");
        applyTeaserCssVars(panel, T);
        panel.style.position = "fixed";
        panel.style.left = rect.left + "px";
        panel.style.top = rect.top + "px";
        panel.style.width = rect.width + "px";
        panel.style.height = rect.height + "px";
        panel.style.margin = "0";

        await nextFrame();
        if (aborted) return;
        var lift = computeDialElevation(rect, vw, vh, opts.isVertical);
        panel.style.transition =
          "transform " + T.DIAL_UP_MS + "ms cubic-bezier(0.22, 1.1, 0.36, 1)";
        panel.style.transform =
          "translate(" + lift.dx + "px," + lift.dy + "px) scale(" + lift.scale + ")";
        await waitMs(T.DIAL_UP_MS);
        if (aborted) return;

        // Slow light wave across the chips.
        var chips = panel.querySelectorAll(getRevealChipSelector(opts.isVertical));
        for (var c = 0; c < chips.length; c++) {
          (function (idx) {
            var startId = setTimeout(function () {
              if (aborted) return;
              chips[idx].classList.add("evo-reveal-chip-flash");
              var endId = setTimeout(function () {
                chips[idx].classList.remove("evo-reveal-chip-flash");
              }, T.WAVE_FLASH_MS);
              timers.push(endId);
            }, idx * T.WAVE_STEP_MS);
            timers.push(startId);
          })(c);
        }
        await waitMs(computeWaveDurationMs(chips.length));
        if (aborted) return;

        // Squeeze: chips slide aside, an empty 11th slot opens at the arrow.
        var secretTierIndex = opts.secretTierIndex != null
          ? opts.secretTierIndex
          : RING_MAX_CHIPS - 1;
        var secretNode;
        if (opts.isVertical) {
          var stripPanel = panel.classList.contains("evo-strip-panel")
            ? panel
            : panel.querySelector(".evo-strip-panel") || panel;
          var strip = stripPanel.querySelector(".evo-strip");
          var stripNodesWrap = stripPanel.querySelector(".evo-strip__nodes");
          var prevRects = captureNodeRects(
            Array.prototype.slice.call(
              stripPanel.querySelectorAll(".evo-strip__node")
            )
          );
          stripPanel.classList.add("evo-strip-panel--squeezed");
          stripPanel.style.setProperty("--evo-strip-count", String(RING_MAX_CHIPS));
          if (strip) {
            strip.classList.add("evo-strip--squeezed");
            strip.style.setProperty("--evo-strip-count", String(RING_MAX_CHIPS));
          }
          secretNode = createStripSecretChipNode(
            document, RING_BASE_SLOTS, secretTierIndex
          );
          secretNode.classList.add("evo-reveal-chip-hidden");
          if (stripNodesWrap) stripNodesWrap.appendChild(secretNode);
          applyStripSqueezeFlip(stripPanel, prevRects, T.SQUEEZE_MS);
        } else {
          var ring = panel.querySelector(".evo-ring");
          if (ring) {
            ring.classList.add("evo-ring--squeezing");
            ring.style.setProperty(
              "--evo-chip-step", getRingChipStepDeg(RING_MAX_CHIPS) + "deg"
            );
            ring.style.setProperty("--evo-count", String(RING_MAX_CHIPS));
            secretNode = createRingSecretChipNode(
              document, RING_BASE_SLOTS, secretTierIndex
            );
            secretNode.classList.add("evo-reveal-chip-hidden");
            var ringNodesWrap = ring.querySelector(".evo-ring__nodes");
            if (ringNodesWrap) ringNodesWrap.appendChild(secretNode);
          }
        }
        await waitMs(T.SQUEEZE_MS);
        if (aborted) return;

        // "?" chip materialises at centre and flies into the new slot.
        var qFlyer = createFlyingChipElement(document, "question");
        popoutMount.appendChild(qFlyer);
        await materializeFlyer(qFlyer, vw, vh, T.TEASER_CHIP_IN_MS);
        if (aborted) return;

        if (secretNode) {
          await flyFlyerToRect(qFlyer, secretNode.getBoundingClientRect(), T.TEASER_CHIP_FLY_MS);
          if (aborted) return;
          secretNode.classList.remove("evo-reveal-chip-hidden");
          flashNode(secretNode, timers, T.TEASER_LAND_FLASH_MS);
        }
        if (qFlyer.parentNode) qFlyer.parentNode.removeChild(qFlyer);
        if (typeof opts.onPlaySound === "function") opts.onPlaySound();

        // Counter flips to e.g. 10/11 while the dial is still elevated.
        if (!opts.isVertical && opts.teaserCount) {
          var countEl = panel.querySelector(".evo-ring__count");
          if (countEl) {
            countEl.innerHTML = buildHubCountHtml(
              opts.teaserCount.current,
              opts.teaserCount.total
            );
            countEl.classList.remove("evo-reveal-count-pop");
            countEl.style.animationDuration = T.TEASER_COUNT_POP_MS + "ms";
            void countEl.offsetWidth;
            countEl.classList.add("evo-reveal-count-pop");
          }
        }

        await waitMs(T.TEASER_LAND_SETTLE_MS);
        if (aborted) return;

        // Return the dial to its chrome slot.
        panel.style.transition =
          "transform " + T.DIAL_DOWN_MS + "ms cubic-bezier(0.4, 0.1, 0.3, 1)";
        panel.style.transform = "translate(0px, 0px) scale(1)";
        await waitMs(T.DIAL_DOWN_MS);
        if (aborted) return;
        restoreElevatedPanel();
      }
      if (typeof opts.onPopulateFinal === "function") opts.onPopulateFinal();
    } else {
      await waitMs(T.LAND_SETTLE_MS);
      if (aborted) return;
      if (typeof opts.onPopulateFinal === "function") opts.onPopulateFinal();
    }

    // --- Overlay out -------------------------------------------------------
    clearDiscoveryShowcase(overlay);
    overlay.classList.remove("secret-reveal-overlay--visible");
    await waitMs(T.SCRIM_OUT_MS);
    if (aborted) return;
    overlay.classList.add("hidden");
    overlay.setAttribute("aria-hidden", "true");
    popoutMount.innerHTML = "";

    finished = true;
    if (typeof opts.onComplete === "function") opts.onComplete();
  })().catch(function () {
    abort();
  });

  return { abort: abort, done: done };
}
